import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarCheck2,
  Check,
  UserRound,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import api from "../api/client";
import { trackProductEvent } from "../analytics";
import BrandMark from "../components/BrandMark";
import { ErrorNotice, LoadingState } from "../components/Feedback";
import Seo from "../components/Seo";
import type { LeaveType, Organization, Paginated } from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import DateField from "../components/DateField";

type Mode = "personal" | "organization";
type LeaveConfig = {
  leave_type_id: number;
  start_date: string;
  start_balance_hours: string;
  accrual_frequency: "monthly" | "yearly" | "manual";
  accrual_hours: string;
  expiry_months: string;
  carry_over_max_hours: string;
  carry_over_hours: string;
  carry_over_source_year: string;
  carry_over_expires_on: string;
};

const weekDays = [
  "Maandag",
  "Dinsdag",
  "Woensdag",
  "Donderdag",
  "Vrijdag",
  "Zaterdag",
  "Zondag",
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get("plan");
  const initialMode =
    requestedPlan === "team" || requestedPlan === "business" ? "organization" : null;
  const year = new Date().getFullYear();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState<Mode | null>(initialMode);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [schoolRegion, setSchoolRegion] = useState("middle");
  const [optionalHoliday, setOptionalHoliday] = useState(false);
  const [hours, setHours] = useState(["8", "8", "4", "8", "8", "0", "0"]);
  const [leaveConfigs, setLeaveConfigs] = useState<LeaveConfig[]>([]);
  const [organizationName, setOrganizationName] = useState("");
  const [teamName, setTeamName] = useState("Algemeen");
  const [planCode, setPlanCode] = useState(
    requestedPlan === "business" ? "business" : "team",
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void api
      .get<Paginated<LeaveType> | LeaveType[]>("/leave/types/")
      .then((response) => {
        const list = unwrapResults(response.data);
        setLeaveTypes(list);
        setLeaveConfigs(
          list.map((leaveType) => {
            const primary = leaveType.category === "leave";
            return {
              leave_type_id: leaveType.id,
              start_date: `${year}-01-01`,
              start_balance_hours: primary ? "200" : "0",
              accrual_frequency: "yearly",
              accrual_hours: "0",
              expiry_months: primary ? "60" : "24",
              carry_over_max_hours: primary ? "80" : "0",
              carry_over_hours: primary ? "16" : "0",
              carry_over_source_year: String(year - 1),
              carry_over_expires_on: `${year}-07-01`,
            };
          }),
        );
      })
      .catch((requestError: unknown) =>
        setError(
          apiErrorMessage(
            requestError,
            "De onboardinggegevens konden niet worden geladen.",
          ),
        ),
      )
      .finally(() => setLoading(false));
  }, [year]);

  const weeklyHours = useMemo(
    () => hours.reduce((total, value) => total + (Number(value) || 0), 0),
    [hours],
  );
  const updateConfig = (id: number, key: keyof LeaveConfig, value: string) =>
    setLeaveConfigs((current) =>
      current.map((row) => (row.leave_type_id === id ? { ...row, [key]: value } : row)),
    );

  const nextFromMode = () => {
    if (!mode) return;
    setError("");
    setStep(2);
  };

  const complete = async (event: FormEvent) => {
    event.preventDefault();
    if (!mode) return;
    if (mode === "organization" && step === 2) {
      setStep(3);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/onboarding/complete/", {
        school_region: schoolRegion,
        optional_holidays_enabled: optionalHoliday,
        schedules: hours.map((value, weekday) => ({
          parity: "all",
          weekday,
          hours: value || "0",
        })),
        leave_configs: leaveConfigs.map((row) => ({
          ...row,
          carry_over_source_year: Number(row.carry_over_source_year),
        })),
      });

      if (mode === "personal") {
        navigate("/app", { replace: true });
        return;
      }

      const organizationResponse = await api.post<Organization>("/organizations/", {
        name: organizationName,
        plan_code: planCode,
        team_name: teamName.trim(),
        school_region: schoolRegion,
      });
      const organizationId = organizationResponse.data.id;
      window.localStorage.setItem("verlofkaart.organization", String(organizationId));
      const checkoutResponse = await api.post("/billing/checkout/", {
        organization: organizationId,
        plan_code: planCode,
        billing_interval: "monthly",
        idempotency_key: crypto.randomUUID(),
      });
      trackProductEvent("checkout_started");

      const checkoutUrl = checkoutResponse.data.checkout_url as string | undefined;
      if (checkoutUrl) window.location.assign(checkoutUrl);
      else navigate("/app", { replace: true });
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De onboarding kon niet worden afgerond."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState label="Onboarding laden..." />;

  return (
    <div className="onboarding-page">
      <Seo
        title="Account instellen"
        path="/onboarding"
        description="Stel je DeVerlofkaart-account in."
        noIndex
      />
      <header className="onboarding-header">
        <BrandMark />
        <span>
          Stap {step} van {mode === "organization" ? 3 : 2}
        </span>
      </header>
      <main className="onboarding-main">
        <div className="onboarding-progress" aria-hidden="true">
          <i className="done" />
          <i className={step >= 2 ? "done" : ""} />
          <i className={mode === "organization" && step >= 3 ? "done" : ""} />
        </div>
        {error && <ErrorNotice>{error}</ErrorNotice>}

        {step === 1 && (
          <section className="onboarding-step">
            <div className="step-heading">
              <p className="eyebrow">Gebruiksscenario</p>
              <h1>Hoe wil je DeVerlofkaart gebruiken?</h1>
              <p>
                Je kunt later altijd een organisatie toevoegen zonder je persoonlijke
                verlofkaart kwijt te raken.
              </p>
            </div>
            <div className="mode-grid">
              <button
                type="button"
                className={mode === "personal" ? "selected" : ""}
                onClick={() => setMode("personal")}
              >
                <span>
                  <UserRound size={26} />
                </span>
                <h2>Voor mezelf</h2>
                <p>Gratis. Beheer je eigen saldi, werkpatroon en jaarplanning.</p>
                <strong>
                  <Check size={16} /> Free
                </strong>
              </button>
              <button
                type="button"
                className={mode === "organization" ? "selected" : ""}
                onClick={() => setMode("organization")}
              >
                <span>
                  <Building2 size={26} />
                </span>
                <h2>Voor mijn organisatie</h2>
                <p>Nodig medewerkers uit, werk met teams en fiatteer aanvragen.</p>
                <strong>
                  <Check size={16} /> Team of Business
                </strong>
              </button>
            </div>
            <button
              className="button primary step-next"
              type="button"
              disabled={!mode}
              onClick={nextFromMode}
            >
              Doorgaan <ArrowRight size={17} />
            </button>
          </section>
        )}

        {step === 2 && (
          <form className="onboarding-step" onSubmit={complete}>
            <div className="step-heading">
              <p className="eyebrow">Persoonlijke basis</p>
              <h1>Stel je werkweek en saldi in.</h1>
              <p>
                Uren zijn de rekeneenheid. Je kunt deze instellingen later wijzigen met
                een ingangsdatum.
              </p>
            </div>
            <div className="setup-grid">
              <section className="surface setup-section">
                <h2>
                  Werkpatroon <span>{weeklyHours} uur/week</span>
                </h2>
                <div className="workweek-inputs">
                  {weekDays.map((day, index) => (
                    <label key={day}>
                      <span>{day}</span>
                      <span className="number-suffix">
                        <input
                          type="number"
                          min="0"
                          max="24"
                          step="0.25"
                          value={hours[index]}
                          onChange={(event) =>
                            setHours((current) =>
                              current.map((value, dayIndex) =>
                                dayIndex === index ? event.target.value : value,
                              ),
                            )
                          }
                        />
                        <small>uur</small>
                      </span>
                    </label>
                  ))}
                </div>
              </section>
              <section className="surface setup-section">
                <h2>Kalenderlagen</h2>
                <label>
                  <span>Schoolvakantieregio</span>
                  <select
                    value={schoolRegion}
                    onChange={(event) => setSchoolRegion(event.target.value)}
                  >
                    <option value="north">Noord</option>
                    <option value="middle">Midden</option>
                    <option value="south">Zuid</option>
                  </select>
                </label>
                <label className="toggle-field">
                  <input
                    type="checkbox"
                    checked={optionalHoliday}
                    onChange={(event) => setOptionalHoliday(event.target.checked)}
                  />
                  <span>
                    <strong>Optionele feestdagen meenemen</strong>
                    <small>
                      Bijvoorbeeld Bevrijdingsdag volgens je persoonlijke beleid.
                    </small>
                  </span>
                </label>
                <div className="school-rule">
                  <CalendarCheck2 size={19} />
                  <p>
                    Schoolvakanties zijn alleen informatief en hebben altijd nul uur
                    saldo-impact.
                  </p>
                </div>
              </section>
            </div>
            <section className="surface setup-section balances-setup">
              <h2>Startsaldos en transport</h2>
              <div className="balance-setup-head">
                <span>Type</span>
                <span>Nieuw toegekend</span>
                <span>Transport {year - 1}</span>
                <span>Vervaldatum</span>
              </div>
              {leaveConfigs.map((config) => {
                const leaveType = leaveTypes.find(
                  (row) => row.id === config.leave_type_id,
                );
                return (
                  <div className="balance-setup-row" key={config.leave_type_id}>
                    <strong>{leaveType?.name}</strong>
                    <label>
                      <span className="mobile-label">Nieuw toegekend</span>
                      <span className="number-suffix">
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={config.start_balance_hours}
                          onChange={(event) =>
                            updateConfig(
                              config.leave_type_id,
                              "start_balance_hours",
                              event.target.value,
                            )
                          }
                        />
                        <small>uur</small>
                      </span>
                    </label>
                    <label>
                      <span className="mobile-label">Transport</span>
                      <span className="number-suffix">
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={config.carry_over_hours}
                          onChange={(event) =>
                            updateConfig(
                              config.leave_type_id,
                              "carry_over_hours",
                              event.target.value,
                            )
                          }
                        />
                        <small>uur</small>
                      </span>
                    </label>
                    <label>
                      <span className="mobile-label">Vervaldatum</span>
                      <DateField
                        value={config.carry_over_expires_on}
                        onChange={(value) =>
                          updateConfig(
                            config.leave_type_id,
                            "carry_over_expires_on",
                            value,
                          )
                        }
                      />
                    </label>
                  </div>
                );
              })}
            </section>
            <div className="step-actions">
              <button
                className="button subtle"
                type="button"
                onClick={() => setStep(1)}
              >
                <ArrowLeft size={17} /> Terug
              </button>
              <button className="button primary" type="submit">
                {mode === "organization"
                  ? "Organisatie instellen"
                  : "Onboarding afronden"}{" "}
                <ArrowRight size={17} />
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form className="onboarding-step organization-step" onSubmit={complete}>
            <div className="step-heading">
              <p className="eyebrow">Organisatie</p>
              <h1>Maak je teamwerkruimte aan.</h1>
              <p>
                De eigenaar kan later policies, medewerkers, rollen en betaalgegevens
                beheren.
              </p>
            </div>
            <section className="surface setup-section">
              <label>
                <span>Organisatienaam</span>
                <input
                  value={organizationName}
                  onChange={(event) => setOrganizationName(event.target.value)}
                  placeholder="Bijvoorbeeld AnchorWorks B.V."
                  required
                  autoFocus
                />
              </label>
              <label>
                <span>Eerste team</span>
                <input
                  value={teamName}
                  onChange={(event) => setTeamName(event.target.value)}
                  placeholder="Bijvoorbeeld Algemeen"
                  required
                />
              </label>
              <fieldset>
                <legend>Pakket</legend>
                <div className="plan-choice">
                  <label className={planCode === "team" ? "selected" : ""}>
                    <input
                      type="radio"
                      name="plan"
                      value="team"
                      checked={planCode === "team"}
                      onChange={() => setPlanCode("team")}
                    />
                    <span>
                      <strong>Team</strong>
                      <small>Aanvragen, fiattering, teamplanning en rapportages.</small>
                    </span>
                  </label>
                  <label className={planCode === "business" ? "selected" : ""}>
                    <input
                      type="radio"
                      name="plan"
                      value="business"
                      checked={planCode === "business"}
                      onChange={() => setPlanCode("business")}
                    />
                    <span>
                      <strong>Business</strong>
                      <small>
                        Meerdere teams, managers, afdelingen en uitgebreidere audit.
                      </small>
                    </span>
                  </label>
                </div>
              </fieldset>
              <p className="checkout-note">
                Je wordt alleen naar Mollie gestuurd wanneer een eerste betaling of
                betaalmandaat nodig is.
              </p>
            </section>
            <div className="step-actions">
              <button
                className="button subtle"
                type="button"
                onClick={() => setStep(2)}
              >
                <ArrowLeft size={17} /> Terug
              </button>
              <button className="button primary" type="submit" disabled={submitting}>
                {submitting ? "Werkruimte maken..." : "Werkruimte maken"}
                <ArrowRight size={17} />
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
